import { useState } from "react";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { SERVICES } from "@/data/site";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const fieldCls = "h-12 rounded-none border-slate-300 focus-visible:ring-gold bg-white";

const EnquiryForm = ({ mode = "quote" }) => {
  const isQuote = mode === "quote";
  const [form, setForm] = useState({ name: "", organization: "", email: "", phone: "", service: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target ? e.target.value : e });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/enquiries", { type: mode, ...form });
      toast.success(isQuote ? "Quote request submitted." : "Message sent.", {
        description: "Our team will get in touch with you shortly.",
      });
      setForm({ name: "", organization: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      toast.error("Submission failed", { description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-testid={`${mode}-form`}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div className="space-y-2">
          <Label htmlFor={`${mode}-name`}>Full Name *</Label>
          <Input id={`${mode}-name`} data-testid={`${mode}-name-input`} required value={form.name} onChange={set("name")} placeholder="Your name" className={fieldCls} />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${mode}-phone`}>Phone *</Label>
          <Input id={`${mode}-phone`} data-testid={`${mode}-phone-input`} required value={form.phone} onChange={set("phone")} placeholder="+91 ..." className={fieldCls} />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div className="space-y-2">
          <Label htmlFor={`${mode}-email`}>Email *</Label>
          <Input id={`${mode}-email`} type="email" data-testid={`${mode}-email-input`} required value={form.email} onChange={set("email")} placeholder="you@company.com" className={fieldCls} />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${mode}-org`}>{isQuote ? "Organization *" : "Organization"}</Label>
          <Input id={`${mode}-org`} data-testid={`${mode}-organization-input`} required={isQuote} value={form.organization} onChange={set("organization")} placeholder="Company / Institution" className={fieldCls} />
        </div>
      </div>
      {isQuote && (
        <div className="space-y-2">
          <Label>Service Required *</Label>
          <Select required value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
            <SelectTrigger className={fieldCls} data-testid="quote-service-select">
              <SelectValue placeholder="Select a service" />
            </SelectTrigger>
            <SelectContent>
              {SERVICES.map((s) => (
                <SelectItem key={s.slug} value={s.title} data-testid={`quote-service-${s.slug}`}>{s.title}</SelectItem>
              ))}
              <SelectItem value="Other / Multiple Services" data-testid="quote-service-other">Other / Multiple Services</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
      <div className="space-y-2">
        <Label htmlFor={`${mode}-message`}>{isQuote ? "Requirement Details *" : "Message *"}</Label>
        <Textarea
          id={`${mode}-message`}
          data-testid={`${mode}-message-input`}
          required
          rows={5}
          value={form.message}
          onChange={set("message")}
          placeholder={isQuote ? "Tell us about your manpower requirement — number of personnel, location, duration..." : "How can we help you?"}
          className="rounded-none border-slate-300 focus-visible:ring-gold bg-white"
        />
      </div>
      <button
        type="submit"
        disabled={submitting}
        data-testid={`${mode}-form-submit-btn`}
        className="w-full inline-flex items-center justify-center gap-2 bg-navy hover:bg-navy-light text-white font-heading font-bold text-xs tracking-widest uppercase px-7 py-4 transition-colors disabled:opacity-60"
      >
        {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} />}
        {submitting ? "Submitting..." : isQuote ? "Submit Quote Request" : "Send Message"}
      </button>
    </form>
  );
};

export default EnquiryForm;
